import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import GppGoodIcon from "@mui/icons-material/GppGood";
import ShieldIcon from "@mui/icons-material/Shield";
import BlockIcon from "@mui/icons-material/Block";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import {
  fetchFirewallStatus,
  quickBlockIp,
  quickUnblockIp,
} from "../features/firewall/sipAclSlice.js";

const IPV4_REGEX =
  /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;

const StatPill = ({ icon, label, value, color }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      px: 1.75,
      py: 1,
      borderRadius: 2,
      backgroundColor: alpha(color, 0.08),
      border: `1px solid ${alpha(color, 0.16)}`,
    }}
  >
    {icon}
    <Box>
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, color }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const SecurityOperationsPanel = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { loading = false, status } = useSelector((state) => state.firewall ?? {});
  const [ipToBlock, setIpToBlock] = useState("");

  useEffect(() => {
    dispatch(fetchFirewallStatus());
  }, [dispatch]);

  const fail2ban = status?.fail2ban ?? {
    enabled: false,
    currentlyBanned: 0,
    totalBanned: 0,
    currentlyFailed: 0,
    totalFailed: 0,
    bannedIps: [],
  };
  const blockedIps = status?.blockedIps ?? [];
  const recentThreats = status?.recentThreats ?? [];

  const validIp = useMemo(() => IPV4_REGEX.test(ipToBlock.trim()), [ipToBlock]);

  const refreshStatus = async () => {
    try {
      await dispatch(fetchFirewallStatus()).unwrap();
    } catch (error) {
      enqueueSnackbar(error || "Failed to refresh security status", {
        variant: "error",
      });
    }
  };

  const handleQuickBlock = async (ip, source = "manual") => {
    try {
      await dispatch(
        quickBlockIp({
          ip,
          name: source === "manual" ? `Blocked IP ${ip}` : `Blocked scanner ${ip}`,
          description:
            source === "manual"
              ? "Blocked from dashboard security panel"
              : "Blocked from recent SIP threat activity",
        })
      ).unwrap();
      enqueueSnackbar(`Blocked ${ip}`, { variant: "success" });
      setIpToBlock("");
      await dispatch(fetchFirewallStatus()).unwrap();
    } catch (error) {
      enqueueSnackbar(error || `Failed to block ${ip}`, { variant: "error" });
    }
  };

  const handleQuickUnblock = async (ip) => {
    try {
      await dispatch(quickUnblockIp(ip)).unwrap();
      enqueueSnackbar(`Unblocked ${ip}`, { variant: "success" });
      await dispatch(fetchFirewallStatus()).unwrap();
    } catch (error) {
      enqueueSnackbar(error || `Failed to unblock ${ip}`, { variant: "error" });
    }
  };

  return (
    <Card
      sx={{
        mt: 3,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
          sx={{ mb: 2.5 }}
        >
          <Box>
            <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 700 }}>
              <ShieldIcon color="error" />
              Security Operations
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Review live SIP scan activity, block suspicious IPs immediately, and manage active bans from the dashboard.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh Security Status">
              <span>
                <IconButton onClick={refreshStatus} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              component={RouterLink}
              to="/settings/firewall"
              variant="outlined"
              startIcon={<SettingsSuggestIcon />}
              sx={{ textTransform: "none" }}
            >
              Firewall Rules
            </Button>
          </Stack>
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          sx={{ mb: 2.5, flexWrap: "wrap" }}
        >
          <StatPill
            icon={<GppGoodIcon sx={{ color: theme.palette.success.main, fontSize: 18 }} />}
            label="Active Bans"
            value={fail2ban.currentlyBanned}
            color={theme.palette.success.main}
          />
          <StatPill
            icon={<WarningAmberIcon sx={{ color: theme.palette.warning.main, fontSize: 18 }} />}
            label="Recent Failed Attempts"
            value={fail2ban.currentlyFailed}
            color={theme.palette.warning.main}
          />
          <StatPill
            icon={<TravelExploreIcon sx={{ color: theme.palette.error.main, fontSize: 18 }} />}
            label="Tracked Threat IPs"
            value={recentThreats.length}
            color={theme.palette.error.main}
          />
        </Stack>

        {!fail2ban.enabled && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Fail2Ban status could not be read from the server. Quick ACL blocking still works, but live ban visibility may be incomplete.
          </Alert>
        )}

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "flex-start",
            flexDirection: { xs: "column", md: "row" },
            mb: 2.5,
          }}
        >
          <TextField
            label="Quick Block IP"
            placeholder="e.g. 203.0.113.45"
            value={ipToBlock}
            onChange={(event) => setIpToBlock(event.target.value)}
            size="small"
            fullWidth
            helperText="Creates a deny ACL rule and applies a live ban."
          />
          <Button
            variant="contained"
            color="error"
            startIcon={<BlockIcon />}
            disabled={!validIp || loading}
            onClick={() => handleQuickBlock(ipToBlock.trim())}
            sx={{ textTransform: "none", minWidth: 150, mt: { xs: 0, md: "4px" } }}
          >
            Block IP
          </Button>
        </Box>

        <Stack direction={{ xs: "column", lg: "row" }} spacing={3}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.25 }}>
              Recent Threat Activity
            </Typography>
            <Stack spacing={1}>
              {recentThreats.length === 0 && (
                <Alert severity="success">No recent SIP scanner activity was found in the latest Asterisk log sample.</Alert>
              )}
              {recentThreats.slice(0, 8).map((threat) => {
                const alreadyBlocked = blockedIps.some((item) => item.ip === threat.ip);
                return (
                  <Box
                    key={threat.ip}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                      backgroundColor: alpha(theme.palette.error.main, 0.03),
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: "monospace" }}>
                          {threat.ip}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {threat.hits} failed requests
                          {threat.lastSeen ? ` • last seen ${threat.lastSeen}` : ""}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1 }}>
                          {threat.methods.map((method) => (
                            <Chip key={`${threat.ip}-${method}`} label={method} size="small" />
                          ))}
                          {threat.reasons.slice(0, 2).map((reason) => (
                            <Chip
                              key={`${threat.ip}-${reason}`}
                              label={reason}
                              size="small"
                              variant="outlined"
                              color="warning"
                            />
                          ))}
                        </Box>
                      </Box>
                      <Button
                        variant={alreadyBlocked ? "outlined" : "contained"}
                        color={alreadyBlocked ? "success" : "error"}
                        startIcon={alreadyBlocked ? <GppGoodIcon /> : <BlockIcon />}
                        disabled={alreadyBlocked || loading}
                        onClick={() => handleQuickBlock(threat.ip, "threat")}
                        sx={{ textTransform: "none", alignSelf: { xs: "flex-start", sm: "center" } }}
                      >
                        {alreadyBlocked ? "Already blocked" : "Block"}
                      </Button>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", lg: "block" } }} />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.25 }}>
              Active Blocked IPs
            </Typography>
            <Stack spacing={1}>
              {blockedIps.length === 0 && (
                <Alert severity="info">No IPs are currently blocked through ACL or live bans.</Alert>
              )}
              {blockedIps.slice(0, 10).map((item) => (
                <Box
                  key={`${item.ip}-${item.source}`}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                    backgroundColor: alpha(theme.palette.success.main, 0.03),
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: "monospace" }}>
                        {item.ip}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.source === "live-ban"
                          ? "Currently banned in Fail2Ban"
                          : "Blocked by persistent ACL rule"}
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<LockOpenIcon />}
                      disabled={loading}
                      onClick={() => handleQuickUnblock(item.ip)}
                      sx={{ textTransform: "none", alignSelf: { xs: "flex-start", sm: "center" } }}
                    >
                      Unblock
                    </Button>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default SecurityOperationsPanel;
